import ActionTextIcon from "@/components/icons/ActionTextIcon";
import { List } from "@/components/list/List";
import { paginationStandardProps } from "@/utils/componentUtils";
import { isDefined } from "@/utils/utils";
import { Flex, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDownloadContractVehiclesExport } from "../../../../../../common/api/queries";
import { ExportFileType } from "../../../../../../common/enums";
import { Insurance, InsuranceContract } from "../../../../types";
import {
  isGenericInsurance,
  isLifeInsurance,
  isOtherVehicleInsurance,
  isRealtyInsurance,
  isTravelInsurance,
  isVehicleInsurance,
} from "../../../../utils";
import GenericInsuranceView from "./generic/GenericInsuranceView";
import { InsuranceCollapseView } from "./InsuranceCollapseView";
import { InsurancesToggleAllButton } from "./InsurancesToggleAllButton";
import LifeInsuranceView from "./life/LifeInsuranceView";
import RealtyInsuranceView from "./realty/RealtyInsuranceView";
import TravelInsuranceView from "./travel/TravelInsuranceView";
import { OtherVehicleInsuranceView } from "./vehicle/OtherVehicleInsuranceView";
import { VehicleInsuranceView } from "./vehicle/VehicleInsuranceView";

const getInsuranceView = (insurance: Insurance, contract: InsuranceContract) => {
  if (isVehicleInsurance(insurance)) {
    return (
      <VehicleInsuranceView
        insurance={insurance}
        vehicleHolder={contract.clients[insurance.vehicleHolderIndex]}
        vehicleOwner={contract.clients[insurance.vehicleOwnerIndex]}
      />
    );
  } else if (isOtherVehicleInsurance(insurance)) {
    return (
      <OtherVehicleInsuranceView
        insurance={insurance}
        vehicleHolder={contract.clients[insurance.vehicleHolderIndex]}
        vehicleOwner={contract.clients[insurance.vehicleOwnerIndex]}
      />
    );
  } else if (isRealtyInsurance(insurance)) {
    return (
      <RealtyInsuranceView
        insurance={insurance}
        insuredClient={contract.clients[insurance.insuredClientIndex]}
        vinculationClient={
          isDefined(insurance.vinculationClientIndex) ? contract.clients[insurance.vinculationClientIndex] : undefined
        }
      />
    );
  } else if (isTravelInsurance(insurance)) {
    return <TravelInsuranceView insurance={insurance} />;
  } else if (isLifeInsurance(insurance)) {
    return <LifeInsuranceView insurance={insurance} insuredClient={contract.clients[insurance.insuredClientIndex]} />;
  } else if (isGenericInsurance(insurance)) {
    return (
      <GenericInsuranceView insurance={insurance} insuredClient={contract.clients[insurance.insuredClientIndex]} />
    );
  }

  return null;
};

interface Props {
  contract: InsuranceContract;
}

export const NonLifeInsurancesData = ({ contract }: Props) => {
  const { t } = useTranslation();

  const [openedInsurancesKeys, setOpenedInsurancesKeys] = useState<string[]>([]);

  const downloadContractVehiclesExport = useDownloadContractVehiclesExport();
  const multipleInsurances = contract.insurances.length > 1;
  const hasVehicleInsurance = contract.insurances.some((insurance) => isVehicleInsurance(insurance));

  return (
    <>
      <Flex justify={hasVehicleInsurance ? "space-between" : "end"} align="end" className="margin-bottom-tiny">
        {hasVehicleInsurance && (
          <Space size="middle">
            <ActionTextIcon
              icon="download"
              color="blue"
              text={t("common.exportXlsx")}
              onClick={() =>
                downloadContractVehiclesExport.mutate({
                  id: contract.id,
                  object: { exportFileType: ExportFileType.XLSX },
                })
              }
            />

            <ActionTextIcon
              icon="download"
              color="green"
              text={t("common.exportCsv")}
              onClick={() =>
                downloadContractVehiclesExport.mutate({
                  id: contract.id,
                  object: { exportFileType: ExportFileType.CSV },
                })
              }
            />
          </Space>
        )}

        <InsurancesToggleAllButton
          insurances={contract.insurances}
          activeKeys={openedInsurancesKeys}
          onActiveKeysChange={setOpenedInsurancesKeys}
        />
      </Flex>

      <List
        items={contract.insurances}
        renderItem={(insurance, index) => (
          <InsuranceCollapseView
            insurance={insurance}
            contract={contract}
            index={index}
            multipleInsurances={multipleInsurances}
            activeKeys={openedInsurancesKeys}
            onActiveKeysChange={setOpenedInsurancesKeys}
          >
            {getInsuranceView(insurance, contract)}
          </InsuranceCollapseView>
        )}
        pagination={
          contract.insurances.length > 5
            ? {
                ...paginationStandardProps,
                showQuickJumper: false,
                pageSize: 5,
                size: "small",
              }
            : false
        }
        gap={8}
      />
    </>
  );
};
